import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import FirstIcon from "@material-ui/icons/PhotoLibrary";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import classNames from "classnames";
import React from "react";

const useStyles = makeStyles(
  theme => ({
    image: {
      height: "100%",
      objectFit: "contain",
      userSelect: "none",
      width: "100%"
    },

    imageContainer: {
      "&:hover, &.dragged": {
        "& $imageOverlay": {
          display: "block"
        }
      },
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(),
      height: 148,
      overflow: "hidden",
      padding: theme.spacing(2),
      position: "relative",
      width: 148
    },
    imageInVisible: {
      background: "rgba(0, 0, 0, 0.3)",
      cursor: "move",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: 148,
      left: 0,
      position: "absolute",
      top: 0,
      width: 148
    },
    imageOverlay: {
      background: "rgba(0, 0, 0, 0.6)",
      cursor: "move",
      display: "none",
      height: 148,
      left: 0,
      position: "absolute",
      top: 0,
      width: 148
    },
    imageOverlayShow: {
      "&$imageOverlay": {
        alignItems: "center",
        display: "flex",
        justifyContent: "center"
      }
    },
    imageOverlayToolbar: {
      display: "flex",
      justifyContent: "flex-end",
      flexWrap: "wrap" 
    },
    defaultimg: {
      width: 148,
      position: "absolute",
      bottom: -3,
      left: 0,
      color: fade(theme.palette.text.secondary, 0.6),
      display: "block",
      textAlign: "center"
    }
  }),
  { name: "ImageTile" }
);

interface ImageTileProps {
  image: {
    id?: string;
    alt?: string;
    url: string;
    isFirst?: boolean;
    urlType?: string;
  };
  loading?: boolean;
  isVisible?: boolean;
  onImageDelete?: () => void;
  onImageSetFirst?: () => void;
  onImageEdit?: (event: React.ChangeEvent<any>) => void;
  onVisibleEdit?: (id: string) => void;
}

const ImageTile: React.FC<ImageTileProps> = props => {
  const {
    loading,
    onImageDelete,
    onImageEdit,
    onImageSetFirst,
    onVisibleEdit,
    image,
    isVisible
  } = props;
  const classes = useStyles(props);
  const urlType = image?.urlType ? image.urlType : "image";

  return (
    <div className={classes.imageContainer} data-test="product-image">
      {isVisible && (
        <div className={classes.imageInVisible} data-test="product-image">
          <VisibilityOffIcon color={"inherit"} fontSize={"large"} />
        </div>
      )}
      <div
        className={classNames(classes.imageOverlay, {
          [classes.imageOverlayShow]: loading
        })}
      >
        {loading ? (
          <CircularProgress size={32} />
        ) : (
          <div className={classes.imageOverlayToolbar}>
            {onImageSetFirst && (
              <IconButton color="primary" onClick={onImageSetFirst}>
                <FirstIcon />
              </IconButton>
            )}
            {onImageEdit && (
              <IconButton
                color="primary"
                onClick={onImageEdit}
                disabled={!(urlType === "image")}
              >
                <EditIcon />
              </IconButton>
            )}
            {onImageDelete && (
              <IconButton color="primary" onClick={onImageDelete}>
                <DeleteIcon />
              </IconButton>
            )}
            {onVisibleEdit && (
              <IconButton color="primary" onClick={() => onVisibleEdit(image.id)}>
                {isVisible ? (
                  <VisibilityIcon color={"primary"} />
                ) : (
                  <VisibilityOffIcon color={"inherit"} />
                )}
              </IconButton>
            )}
          </div>
        )}
      </div>
      {urlType === "image" ? (
        <img className={classes.image} src={image.url} alt={image.alt} />
      ) : (
        <video autoPlay={true} loop={true} className={classes.image}>
          <source className={classes.image} src={image.url}></source>
        </video>
      )}
      {image?.isFirst && <div className={classes.defaultimg}>Default</div>}
    </div>
  );
};
ImageTile.displayName = "ImageTile";
export default ImageTile;
